/**
 * Which pages a tabbed window may offer, and what to call each tab.
 *
 * The "+" menu offers every page this user could reach from the app: the
 * sidebar's own pages first (the nav-config the V2 sidebar renders, items the
 * user has access to, in their nav sections), then every other page the command
 * palette can open (lib/navigablePages — palette-only pages have no sidebar
 * entry; before TRUS-638 the picker could not open them). Both halves go
 * through page access (useSidebarPageAccess) and a palette page the nav-config
 * marks inaccessible is never offered. Triveny has no nav-config — its rail is
 * static — so it offers the rail's pages, then its palette pages.
 *
 * Labels reuse the sidebar's own (server-resolved, translated) nav labels;
 * pages that are not in the nav use the palette's name (through the nav label
 * bundle when the language has one), and labelFor falls back to the V2
 * page-bar title, so a tab never shows a raw id when the app has a name for it.
 */
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavConfig } from '@/hooks/useNavConfig';
import { useSidebarPageAccess } from '@/hooks/useSidebarPageAccess';
import { useNavigablePages } from '@/hooks/useNavigablePages';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { resolveNavLabel, resolveNavSectionLabel } from '@/utils/navLabel';
import { V2_TAB_META } from '@/components/v2/v2TabMeta';
import { NAVIGABLE_PAGES, getPageDescriptor, navigablePageTarget, type NavigablePage } from '@/lib/navigablePages';
import { RAIL_GROUPS } from '@/src/platform/shared/components/Layout/TrivenyRail';
import { isValidTabId, type AppTabSpec } from '@/lib/popout';
import { config } from '@/src/config';
import type { PageOption } from './PagePickerMenu';

const CATALOG_BY_ID = new Map(NAVIGABLE_PAGES.map((p) => [p.id, p]));

/**
 * The Inbox tab is always "Inbox" (TRUS-638): it used to retitle itself after
 * the open conversation ("Channel 123", or a DM's internal dm-… name) as you
 * moved between conversations. A tab opened on the Mailbox keeps that name.
 * Null for any other page.
 */
export function messagingTabLabel(
  spec: AppTabSpec,
  t: (key: string, fallback: string) => string,
): string | null {
  if (spec.tab !== 'workspace-messaging') return null;
  return spec.params?.view === 'email'
    ? t('messagingPopout.tab_mailbox', 'Mailbox')
    : t('messagingPopout.tab_inbox', 'Inbox');
}

export function usePopoutPages(
  organizationId: number | null,
  orgType: 'cbo' | 'employer' | 'sponsor',
  userRole?: string | null,
): { pages: PageOption[]; labelFor: (spec: AppTabSpec) => string } {
  const { t } = useTranslation();
  const { t: appT, language } = useAppTranslation();
  // Triveny's platform JWT is not accepted by these endpoints (a 401 there
  // signs the user out), so it never asks.
  const navOrg = config.isTriveny ? null : organizationId;
  const { sections, loading: navLoading } = useNavConfig(navOrg, undefined, orgType);
  const { hasPageAccess, loading: accessLoading } = useSidebarPageAccess(navOrg);
  // The palette's pages, with its product / role / org / capability gating.
  const catalog = useNavigablePages(organizationId, userRole);

  const pages = useMemo<PageOption[]>(() => {
    const seen = new Set<string>();
    const out: PageOption[] = [];
    const add = (page: PageOption) => {
      if (!isValidTabId(page.tab) || seen.has(page.tab)) return;
      seen.add(page.tab);
      const known = CATALOG_BY_ID.get(page.tab);
      out.push({
        ...page,
        icon: page.icon ?? known?.icon,
        keywords: page.keywords ?? known?.keywords,
      });
    };
    const fromCatalog = (p: NavigablePage): PageOption => ({
      tab: navigablePageTarget(p.id, config.isTriveny),
      group: p.group,
      icon: p.icon,
      keywords: p.keywords,
      label: resolveNavLabel({ serverLabel: p.label, bundleKey: `nav.item.${p.id}`, fallback: p.label }, language, appT),
    });

    if (config.isTriveny) {
      for (const g of RAIL_GROUPS) for (const it of g.items) add({ tab: it.tab, label: it.label, group: g.label, icon: it.icon });
      for (const p of catalog) add(fromCatalog(p));
      return out;
    }
    if (accessLoading || (navOrg != null && navLoading)) return [];

    // The sidebar's pages, in its sections and with its labels.
    const denied = new Set<string>();
    for (const section of sections) {
      const group = resolveNavSectionLabel(
        { serverName: section.section_name, serverNameEs: section.section_name_es, sectionKey: section.section_key },
        language,
        appT,
      );
      for (const item of section.items) {
        const tab = item.nav_tab_id;
        if (!item.has_access || !hasPageAccess(tab)) {
          denied.add(tab);
          continue;
        }
        add({
          tab,
          group,
          label: resolveNavLabel(
            { serverLabel: item.label, serverLabelEs: item.label_es, bundleKey: `nav.item.${tab}`, fallback: item.page_key },
            language,
            appT,
          ),
        });
      }
    }
    // Then every other page the command palette can open.
    for (const p of catalog) {
      if (denied.has(p.id) || !hasPageAccess(p.id)) continue;
      add(fromCatalog(p));
    }
    return out;
  }, [sections, hasPageAccess, accessLoading, navLoading, navOrg, catalog, language, appT]);

  const byTab = useMemo(() => new Map(pages.map((p) => [p.tab, p.label])), [pages]);

  const labelFor = useCallback(
    (spec: AppTabSpec): string => {
      const inbox = messagingTabLabel(spec, t);
      if (inbox) return inbox;
      return (
        byTab.get(spec.tab) ||
        V2_TAB_META[spec.tab]?.title ||
        getPageDescriptor(spec.tab)?.label ||
        spec.tab
      );
    },
    [byTab, t],
  );

  return { pages, labelFor };
}
